import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Check, Sparkles, Coins, Mic, Megaphone, Mail, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Link, useNavigate } from "react-router-dom";

const PLANS = [
  {
    id: "free", name: "Free Listener", price: 0, icon: Sparkles, package_id: null,
    features: ["Listen to all stations", "Create up to 3 stations", "AI DJ between songs", "PWA mobile install", "Push notifications"],
  },
  {
    id: "premium", name: "Premium Listener", price: 4.99, icon: Coins, package_id: "premium_1mo",
    features: ["Everything in Free", "Tip Jar enabled on your station", "Ad-free listening", "Higher AI DJ cap (100/mo)", "Up to 5 stations"],
  },
  {
    id: "broadcaster", name: "Broadcaster", price: 9.99, icon: Mic, package_id: "broadcaster_1mo",
    features: ["Everything in Premium", "Up to 10 stations", "1000 tracks per station", "Talk show scheduling", "Higher AI DJ cap (500/mo)", "Geo & demographic analytics"],
  },
];

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busyPlan, setBusyPlan] = useState(null);
  const [err, setErr] = useState("");

  const startCheckout = async (pkgId) => {
    setErr("");
    if (!user) {
      // Stash intent and bounce to signup
      try { localStorage.setItem("fliboe_pending_pkg", pkgId); } catch (_) {}
      navigate("/signup");
      return;
    }
    setBusyPlan(pkgId);
    try {
      const { data } = await api.post(
        "/payments/checkout",
        { package_id: pkgId, origin_url: window.location.origin },
        { headers: authHeaders() },
      );
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error("No checkout URL returned");
    } catch (e) {
      console.error("Checkout failed", e);
      const detail = e?.response?.data?.detail || e?.message || "Couldn't start checkout. Try again in a moment.";
      setErr(detail);
      // Surface the error as a persistent banner at top + scroll there so it's never missed
      setTimeout(() => {
        const el = document.querySelector('[data-testid="pricing-error"]');
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 80);
    }
    setBusyPlan(null);
  };

  // If user just signed up with a pending plan, auto-resume checkout
  useEffect(() => {
    if (!user) return;
    const pending = localStorage.getItem("fliboe_pending_pkg");
    if (pending && (pending === "premium_1mo" || pending === "broadcaster_1mo")) {
      localStorage.removeItem("fliboe_pending_pkg");
      startCheckout(pending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const [offer, setOffer] = useState(null);
  useEffect(() => {
    if (!user) { setOffer(null); return; }
    api.get("/offers/welcome", { headers: authHeaders() }).then(r => setOffer(r.data?.active ? r.data : null)).catch(() => {});
  }, [user]);
  const offerPrice = (price) => (offer && price > 0 ? (Math.round(price * (100 - offer.percent)) / 100).toFixed(2) : null);

  const isCurrent = (planId) => user?.plan === planId || (planId === "premium" && user?.plan === "lifetime") || (planId === "broadcaster" && user?.plan === "lifetime");

  return (
    <main className="max-w-6xl mx-auto px-5 py-20" data-testid="pricing-page">
      <div className="text-center mb-12">
        <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-emerald-500 mb-2">// Plans & pricing</div>
        <h1 className="fl-display text-4xl sm:text-6xl font-extrabold tracking-tighter">PICK YOUR FREQUENCY</h1>
        <p className="text-slate-400 mt-3 max-w-xl mx-auto">
          Monthly access, billed once. No recurring charges, no surprise renewals — your plan unlocks for 30 days, then quietly reverts to Free unless you renew.
        </p>
      </div>

      {offer && (
        <div className="max-w-xl mx-auto mb-8 p-4 rounded-xl bg-amber-400/10 border-2 border-dashed border-amber-400/60 text-center" data-testid="pricing-welcome-offer">
          <div className="fl-display text-2xl font-extrabold text-amber-300">{offer.percent}% off your first month</div>
          <div className="text-sm text-slate-300 mt-1">Welcome gift on any plan, applied automatically at checkout · ends {new Date(offer.expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
        </div>
      )}

      {err && (
        <>
          {/* Inline banner — anchored at top of cards */}
          <div className="max-w-md mx-auto mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/40 text-red-200 text-sm" data-testid="pricing-error">
            <div className="fl-mono text-[10px] tracking-widest uppercase text-red-400 mb-1">// Payment error</div>
            {err}
          </div>
          {/* Floating toast — visible even if you scrolled past the banner */}
          <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[120] max-w-sm w-[92vw] bg-red-500/95 text-white text-sm px-4 py-3 rounded-lg shadow-2xl border border-red-300/40 flex items-start gap-2" data-testid="pricing-error-toast">
            <span className="flex-1">{err}</span>
            <button onClick={() => setErr("")} className="text-white/80 hover:text-white shrink-0" aria-label="Dismiss" data-testid="pricing-error-dismiss">✕</button>
          </div>
        </>
      )}

      <div className="grid md:grid-cols-3 gap-5">
        {PLANS.map((p, idx) => {
          const Icon = p.icon;
          const featured = idx === 1;
          const current = isCurrent(p.id);
          return (
            <div key={p.id} className={`fl-card p-7 relative ${featured ? "ring-1 ring-emerald-500/40" : ""}`} data-testid={`plan-card-${p.id}`}>
              {featured && <div className="absolute -top-3 left-7 fl-mono text-[10px] tracking-[0.25em] uppercase bg-emerald-500 text-black px-2 py-1 rounded-full">Most popular</div>}
              {current && <div className="absolute top-4 right-4 fl-mono text-[10px] tracking-[0.25em] uppercase px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">Current plan</div>}
              <Icon className="w-7 h-7 text-emerald-500" />
              <div className="fl-display text-2xl font-bold tracking-tight mt-4">{p.name}</div>
              <div className="mt-3">
                {offerPrice(p.price) ? (
                  <>
                    <span className="fl-display text-5xl font-extrabold text-amber-300" data-testid={`plan-offer-price-${p.id}`}>${offerPrice(p.price)}</span>
                    <span className="text-slate-500 line-through ml-2">${p.price}</span>
                  </>
                ) : <span className="fl-display text-5xl font-extrabold">${p.price}</span>}
                <span className="text-slate-400 ml-1">{p.price === 0 ? "forever" : "/ month"}</span>
              </div>
              <ul className="mt-6 space-y-2.5">
                {p.features.map(f => (
                  <li key={f} className="flex items-start gap-2 text-slate-300 text-sm">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              {p.id === "free" ? (
                user ? (
                  <Link to="/dashboard" className={`fl-btn w-full mt-7 ${featured ? "" : "ghost"}`} data-testid={`plan-cta-${p.id}`}>
                    Open Studio <ArrowRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <Link to="/signup" className={`fl-btn w-full mt-7 ${featured ? "" : "ghost"}`} data-testid={`plan-cta-${p.id}`}>
                    Start free <ArrowRight className="w-4 h-4" />
                  </Link>
                )
              ) : (
                <button
                  onClick={() => startCheckout(p.package_id)}
                  disabled={busyPlan === p.package_id || current}
                  className={`fl-btn w-full mt-7 ${featured ? "" : "ghost"} ${current ? "opacity-60 cursor-not-allowed" : ""}`}
                  data-testid={`plan-cta-${p.id}`}
                >
                  {current ? "Active" : busyPlan === p.package_id ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>Get {p.name.replace(" Listener", "")} <ArrowRight className="w-4 h-4" /></>}
                </button>
              )}
              {p.id !== "free" && (
                <p className="mt-3 fl-mono text-[10px] tracking-widest uppercase text-slate-500 text-center">
                  // One charge · 30 days access · auto-reverts
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-16 grid md:grid-cols-2 gap-5">
        <div className="fl-card p-6">
          <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-emerald-500 mb-2">// Payment & refunds</div>
          <h3 className="fl-display text-2xl font-bold tracking-tight">No subscriptions. No surprises.</h3>
          <p className="mt-3 text-slate-300 text-sm leading-relaxed">
            Pay $4.99 or $9.99 once, get 30 full days of access. After that, your plan automatically reverts to Free
            — no charges on file, nothing to "remember to cancel". If you want another month, just buy another month.
            All payments are processed by Stripe with industry-standard encryption.
          </p>
        </div>

        <div className="fl-card p-6">
          <Megaphone className="w-7 h-7 text-emerald-500" />
          <h3 className="fl-display text-xl font-bold tracking-tight mt-3">Your station. Your brand.</h3>
          <p className="mt-2 text-slate-300 text-sm leading-relaxed">
            Name your station whatever you want and promote it as your own. Custom cover art, your show titles, your hosts.
            Fliboe is the white-label engine — your audience sees your brand, not ours.
          </p>
        </div>
      </div>

      <div className="mt-10 text-center text-xs text-slate-500">
        By upgrading you agree to our <Link to="/terms" className="text-emerald-500 hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-emerald-500 hover:underline">Privacy Policy</Link>.
      </div>
    </main>
  );
}
